// FR-P14 (SD64): upload an existing video or audio file instead of recording one. The file
// goes through exactly the path a recording does — `UploadQueue` (FR-P1: 5 MiB parts straight
// to storage, per-part retry with backoff, resume by part number), `POST /uploads/{id}/complete`,
// then the server's one `publish` job (FR-P3c) — and lands on the same processing screen.
//
// What differs from a recording, deliberately: the bytes already sit on the user's disk, so
// nothing is copied into IndexedDB first (FR-RD7's local-first rule is already met), and a
// closed tab cannot resume the upload because a browser can't reopen a picked file without
// the user. The page warns before unload while an upload is in flight instead.
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Callout, Card, Icon, Input, UploadStatus } from "@grackle/ui";
import { AppShell } from "../components/AppShell";
import { Button, Notice } from "../components/ui";
import { formatBytes } from "../library/format";
import { UploadQueue, type UploadQueueSnapshot } from "../recorder/uploadQueue";
import { requestTrayRefresh } from "../trayRefresh";
import { useSession } from "../session";
import {
  UNSUPPORTED_FILE_MESSAGE,
  UPLOAD_ACCEPT,
  isAudioContentType,
  titleFromFileName,
  uploadContentType,
} from "./mediaFile";
import "./upload.css";

interface Picked {
  file: File;
  contentType: string;
}

function statusLine(s: UploadQueueSnapshot | null): string {
  if (!s || s.phase === "idle" || s.phase === "starting") return "Starting upload…";
  if (s.phase === "done") return "Uploaded";
  if (s.retrying) return "Uploading — retrying in the background";
  return `Uploading — ${formatBytes(s.bytesAccepted)} of ${formatBytes(s.bytesRecorded)}`;
}

export default function UploadPage() {
  const { slug } = useParams();
  const { user, orgs } = useSession();
  const navigate = useNavigate();
  const org = orgs.find((o) => o.slug === slug);

  const [picked, setPicked] = useState<Picked | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [dragging, setDragging] = useState(false);
  const [snapshot, setSnapshot] = useState<UploadQueueSnapshot | null>(null);
  const queueRef = useRef<UploadQueue | null>(null);
  const mountedRef = useRef(true);

  const uploading = snapshot !== null && !["done", "failed", "canceled"].includes(snapshot.phase);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Leaving mid-upload loses it (module comment) — make the browser ask first.
  useEffect(() => {
    if (!uploading) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [uploading]);

  const choose = useCallback((file: File | undefined) => {
    if (!file) return;
    const contentType = uploadContentType(file);
    if (!contentType) {
      setPicked(null);
      setPickError(UNSUPPORTED_FILE_MESSAGE);
      return;
    }
    if (file.size === 0) {
      setPicked(null);
      setPickError("This file is empty.");
      return;
    }
    setPickError(null);
    setSnapshot(null);
    setPicked({ file, contentType });
    setTitle(titleFromFileName(file.name));
  }, []);

  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;
  const canUpload = user.email_verified;

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    choose(e.target.files?.[0]);
    // Let the same file be picked again after a cancel.
    e.target.value = "";
  }

  function onDrop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    setDragging(false);
    if (uploading || !canUpload) return;
    choose(e.dataTransfer.files?.[0]);
  }

  function startUpload() {
    if (!picked || !org) return;
    // A retry after a terminal failure starts a fresh session; abort the dead one's multipart
    // upload rather than leave it behind (best-effort, like every cancel).
    if (queueRef.current) void queueRef.current.cancel();
    const resolvedTitle = title.trim() || titleFromFileName(picked.file.name);
    const queue = new UploadQueue(org.id, resolvedTitle, picked.contentType, {
      sizeBytes: picked.file.size,
      onSnapshot: (s) => {
        if (mountedRef.current) setSnapshot(s);
      },
    });
    queueRef.current = queue;
    queue.start();
    // A File is a Blob: the accumulator slices it lazily into parts, so nothing is read into
    // memory until each part's PUT streams it.
    queue.push(picked.file);
    queue
      .finish()
      .then((grackle) => {
        requestTrayRefresh(); // this org's library just gained a grackle
        if (mountedRef.current) navigate(`/record/${grackle.id}/processing`);
      })
      .catch(() => {
        // The snapshot already carries the failure (or the cancel) for the UI below.
      });
  }

  async function cancelUpload() {
    const queue = queueRef.current;
    queueRef.current = null;
    setSnapshot(null);
    if (queue) await queue.cancel();
  }

  const failed = snapshot?.phase === "failed";
  const kindIcon = picked && isAudioContentType(picked.contentType) ? "waveform" : "video-camera";

  return (
    <AppShell org={org} user={user} section="library" canRecord={canUpload}>
      <div className="lib-header">
        <div className="lib-header-copy">
          <h1 className="lib-header-title">Upload a file</h1>
          <div className="lib-header-sub">
            A video or audio file becomes a recording in {org.name}, with a link, a transcript and notes like any other.
          </div>
        </div>
      </div>

      <div className="upl-body">
        {!canUpload ? (
          <Notice tone="error">Verify your email address before uploading.</Notice>
        ) : !picked ? (
          <label
            className={`upl-drop${dragging ? " is-dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <input
              type="file"
              accept={UPLOAD_ACCEPT}
              className="upl-drop__input"
              onChange={onInputChange}
              data-testid="upload-input"
            />
            <span className="upl-drop__icon" aria-hidden="true">
              <Icon name="cloud-arrow-up" size={28} />
            </span>
            <span className="upl-drop__title">Drop a video or audio file here, or choose one</span>
            <span className="upl-drop__hint">MP4, WebM, MOV, MKV, MP3, M4A, AAC, WAV, OGG or FLAC</span>
          </label>
        ) : (
          <Card className="upl-card">
            <div className="upl-file">
              <span className="upl-file__icon" aria-hidden="true">
                <Icon name={kindIcon} size={22} />
              </span>
              <div className="upl-file__meta">
                <span className="upl-file__name">{picked.file.name}</span>
                <span className="upl-file__size mono">{formatBytes(picked.file.size)}</span>
              </div>
              {!snapshot ? (
                <Button
                  variant="ghost"
                  size="sm"
                  icon="x"
                  onClick={() => setPicked(null)}
                >
                  Choose another
                </Button>
              ) : null}
            </div>

            <Input
              label="Title"
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              disabled={snapshot !== null && !failed}
            />

            {snapshot && !failed ? (
              <>
                <UploadStatus
                  state={snapshot.phase === "done" ? "done" : "uploading"}
                  progress={snapshot.bytesRecorded > 0 ? Math.min(1, snapshot.bytesAccepted / snapshot.bytesRecorded) : 0}
                  label={statusLine(snapshot)}
                />
                <p className="upl-note">Keep this tab open until the upload finishes.</p>
              </>
            ) : null}

            {failed ? (
              snapshot.lastErrorCode === "quota_exceeded" ? (
                // FR-O10: a quota is a limit, not a fault — say so and who can lift it.
                <Callout tone="now" icon="hourglass" data-testid="quota-callout">
                  {snapshot.lastError}
                </Callout>
              ) : (
                <Notice tone="error">{snapshot.lastError ?? "The upload failed."}</Notice>
              )
            ) : null}

            <div className="upl-actions">
              {uploading ? (
                <Button variant="secondary" onClick={() => void cancelUpload()}>
                  Cancel upload
                </Button>
              ) : (
                <Button icon="cloud-arrow-up" onClick={startUpload} disabled={snapshot?.phase === "done"}>
                  {failed ? "Try again" : "Start upload"}
                </Button>
              )}
            </div>
          </Card>
        )}

        {pickError ? <Notice tone="error">{pickError}</Notice> : null}
      </div>
    </AppShell>
  );
}
